/**
 * Status Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for status (Active/Inactive) columns.
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { Chip } from 'components';
import { TableTooltip } from '../../TableTooltip';
import { ChipCell } from '../../ChipCell';
import type { TypeConfig } from '../../../components/types';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import { getCellBooleanValue } from '../cellValue';
import { statusTypeConfig, resolveFieldConfigFn } from '../status';

/**
 * Helper to get status config
 */
const getStatusConfig = (
  colId: string
): { options: TypeConfig['options']; chipColors: TypeConfig['chipColors'] } => {
  const resolved = resolveFieldConfigFn
    ? (resolveFieldConfigFn({ id: colId, type: 'status' }) as {
        kind: string;
        options: TypeConfig['options'];
        chipColors: TypeConfig['chipColors'];
      } | null)
    : {
        kind: 'status',
        options: statusTypeConfig.options,
        chipColors: statusTypeConfig.chipColors,
      };

  if (!resolved || resolved.kind !== 'status') {
    throw new Error(`Failed to resolve status config for column: ${colId}`);
  }

  return resolved;
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Status read-only renderer - displays Active/Inactive chip
 * Uses col.id as the data field name
 */
export const statusReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options, chipColors } = getStatusConfig(col.id);

  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use shared getCellBooleanValue to respect accessorFn (important for custom fields)
      const value = getCellBooleanValue(context, col.id);
      const option = options.find(o => o.id === value) || options[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <TableTooltip title={option.label}>
          <Chip
            sx={{ padding: '4px 16px' }}
            isBordered={false}
            isBackgroundFilled
            isTooltip={false}
            label={option.label}
            color={background}
            background={background}
          />
        </TableTooltip>
      );
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Status editable renderer - displays chip with dropdown for editing
 * Uses col.id as the data field name
 */
export const statusEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options: statusOptions, chipColors } = getStatusConfig(col.id);
  const comboBoxOptions = statusOptions.map(o => ({
    id: String(o.id),
    label: o.label,
  }));

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellBooleanValue(context, col.id);
      const option = statusOptions.find(o => o.id === value) || statusOptions[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <ChipCell
          context={context}
          label={option.label}
          background={background}
          color={background}
          editable
          options={comboBoxOptions}
          onUpdate={selected => {
            if (col.onUpdate) {
              const boolValue = selected.id === 'true';
              col.onUpdate(context.row.original, { [col.id]: boolValue } as Partial<T>);
            }
          }}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const statusReadOnly: ColumnRenderer = statusReadOnlyRenderer;

/** Editable renderer with proper typing */
export const statusEditable: ColumnRenderer = statusEditableRenderer;
